<template>
  <div>
    <v-alert
      v-if="kycBanner"
      :type="kycBanner.type"
      variant="tonal"
      density="comfortable"
      class="mb-4"
    >
      <div class="flex flex-wrap items-center gap-2 mb-1">
        <span class="font-medium">{{ kycBanner.title }}</span>
        <v-chip size="small" variant="tonal" :color="kycBanner.type">
          {{ kycBanner.statusLabel }}
        </v-chip>
      </div>
      <div class="text-sm">{{ kycBanner.message }}</div>
      <div v-if="kycBanner.comment" class="text-sm mt-2 whitespace-pre-line">
        {{ kycBanner.comment }}
      </div>
      <div v-if="kycBanner.informationStatus" class="text-xs text-medium-emphasis mt-2">
        {{ kycBanner.informationStatus }}
      </div>
    </v-alert>

    <v-stepper
      :model-value="step"
      class="bg-transparent"
      flat
      editable
      hide-actions
      mobile-breakpoint="md"
      @update:model-value="goTo"
    >
      <v-stepper-header class="shadow-none">
        <template v-for="(item, index) in steps" :key="item.value">
          <v-divider v-if="index > 0" />
          <v-stepper-item
            :value="item.value"
            :title="item.title"
            :complete="item.complete"
            :error="item.missing"
            :subtitle="item.missing ? t('treezorOnboarding.steps.missingInformation') : undefined"
            :color="item.complete ? 'success' : 'primary'"
          />
        </template>
      </v-stepper-header>

      <!-- Padding keeps step card shadows inside the window's overflow clip. -->
      <v-stepper-window class="mx-0 mb-0 p-1">
        <v-stepper-window-item :value="1">
          <CompanyInformationStep :show-missing="leftSteps.has(1)" @next="onSavedNext(1, $event)" />
        </v-stepper-window-item>
        <v-stepper-window-item :value="2">
          <AccountCreatorStep
            :show-missing="leftSteps.has(2)"
            @previous="goTo(1)"
            @next="onSavedNext(2, $event)"
          />
        </v-stepper-window-item>
        <v-stepper-window-item :value="3">
          <IdentityVerificationStep
            :show-missing="leftSteps.has(3)"
            @previous="goTo(2)"
            @next="goTo(4)"
          />
        </v-stepper-window-item>
        <v-stepper-window-item :value="4">
          <EffectiveBeneficiariesStep
            :show-missing="leftSteps.has(4)"
            @previous="goTo(3)"
            @next="goTo(5)"
          />
        </v-stepper-window-item>
        <v-stepper-window-item :value="5">
          <FileStep
            :show-missing="leftSteps.has(5)"
            @previous="goTo(4)"
            @open-account="steps.forEach((item) => leftSteps.add(item.value))"
          />
        </v-stepper-window-item>
      </v-stepper-window>
    </v-stepper>

    <v-dialog v-model="showReviewDialog" width="400" persistent>
      <v-card>
        <v-card-title>{{ t("treezorOnboarding.documents.reviewConfirmTitle") }}</v-card-title>
        <v-card-text>
          <p class="mb-4">
            {{
              t("treezorOnboarding.documents.reviewConfirmMessage", {
                name: documentToReview
                  ? t(`treezorOnboarding.documents.${documentToReview.labelKey}`)
                  : "",
              })
            }}
          </p>
          <v-form :disabled="!!reviewingDocumentId" @submit.prevent>
            <v-textarea
              v-model.trim="reviewComment"
              :label="t('treezorOnboarding.documents.reviewComment')"
              auto-grow
              rows="2"
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn :disabled="!!reviewingDocumentId" @click="showReviewDialog = false">
            {{ t("common.cancel") }}
          </v-btn>
          <v-btn
            color="success"
            variant="flat"
            :loading="reviewingStatus === 'VALIDATED'"
            :disabled="!!reviewingDocumentId"
            @click="onConfirmReviewDocument('VALIDATED')"
          >
            {{ t("treezorOnboarding.documents.validate") }}
          </v-btn>
          <v-btn
            color="error"
            variant="flat"
            :loading="reviewingStatus === 'REFUSED'"
            :disabled="!!reviewingDocumentId"
            @click="onConfirmReviewDocument('REFUSED')"
          >
            {{ t("treezorOnboarding.documents.refuse") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showDeleteDialog" width="400" :persistent="!!deletingDocumentId">
      <v-card>
        <v-card-title>{{ t("treezorOnboarding.documents.deleteConfirmTitle") }}</v-card-title>
        <v-card-text>
          {{
            t("treezorOnboarding.documents.deleteConfirmMessage", {
              name: documentToDelete
                ? t(`treezorOnboarding.documents.${documentToDelete.labelKey}`)
                : "",
            })
          }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn :disabled="!!deletingDocumentId" @click="showDeleteDialog = false">
            {{ t("common.cancel") }}
          </v-btn>
          <v-btn
            color="error"
            variant="flat"
            :loading="!!deletingDocumentId"
            :disabled="!!deletingDocumentId"
            @click="onConfirmDeleteDocument"
          >
            {{ t("treezorOnboarding.documents.delete") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts" setup>
import { useI18n } from "@/composables/useI18n";
import { useUserStore } from "@/store";
import { computed, ref, toRef, watch } from "vue";
import AccountCreatorStep from "./steps/AccountCreatorStep.vue";
import CompanyInformationStep from "./steps/CompanyInformationStep.vue";
import EffectiveBeneficiariesStep from "./steps/EffectiveBeneficiariesStep.vue";
import FileStep from "./steps/FileStep.vue";
import IdentityVerificationStep from "./steps/IdentityVerificationStep.vue";
import { provideTreezorOnboarding } from "./useTreezorOnboarding";

const props = defineProps<{
  accountId: string;
}>();

const { t } = useI18n();
const user = useUserStore();
const {
  account,
  kycReadyForAdminReview,
  companyInfoComplete,
  accountCreatorComplete,
  identityVerificationComplete,
  effectiveBeneficiariesComplete,
  checkedBeneficiaryIncomplete,
  fileComplete,
  showDeleteDialog,
  documentToDelete,
  showReviewDialog,
  documentToReview,
  reviewComment,
  deletingDocumentId,
  reviewingDocumentId,
  reviewingStatus,
  onConfirmReviewDocument,
  onConfirmDeleteDocument,
} = provideTreezorOnboarding(toRef(props, "accountId"));

const step = ref(1);
const leftSteps = ref(new Set<number>());

const steps = computed(() =>
  [
    {
      value: 1,
      title: t("treezorOnboarding.steps.company"),
      complete: companyInfoComplete.value,
      flagged: false,
    },
    {
      value: 2,
      title: t("treezorOnboarding.steps.you"),
      complete: accountCreatorComplete.value,
      flagged: false,
    },
    {
      value: 3,
      title: t("treezorOnboarding.steps.identity"),
      complete: identityVerificationComplete.value,
      flagged: false,
    },
    {
      value: 4,
      title: t("treezorOnboarding.steps.beneficiaries"),
      complete: effectiveBeneficiariesComplete.value,
      // A red beneficiary chip flags the step too, before leaving it.
      flagged: checkedBeneficiaryIncomplete.value,
    },
    {
      value: 5,
      title: t("treezorOnboarding.steps.file"),
      complete: fileComplete.value,
      flagged: false,
    },
  ].map((item) => ({
    ...item,
    missing: item.flagged || (leftSteps.value.has(item.value) && !item.complete),
  })),
);

const goTo = (target: number) => {
  leftSteps.value.add(step.value);
  for (let value = 1; value < target; value++) leftSteps.value.add(value);
  step.value = target;
};
watch(
  () => props.accountId,
  () => {
    step.value = 1;
    leftSteps.value.clear();
  },
);
// Opening stays mounted across accounts, so a save can resolve after switching to another one.
const onSavedNext = (savedStep: number, savedAccountId: string) => {
  if (savedAccountId === props.accountId && step.value === savedStep) goTo(savedStep + 1);
};

const kycBanner = computed(() => {
  const kyc = account.value?.treezorCompany?.kyc;
  if (kyc && kyc.kycStatus !== "NONE") {
    const statusLabel = t(`treezorOnboarding.kyc.statuses.${kyc.kycStatus}`);
    const comment = kyc.kycReviewComment?.trim() || undefined;
    const informationStatus = kyc.informationStatus?.trim() || undefined;

    switch (kyc.kycStatus) {
      case "PENDING":
        return {
          type: "info" as const,
          title: t("treezorOnboarding.kyc.pendingTitle"),
          message: t("treezorOnboarding.kyc.pendingMessage"),
          statusLabel,
          comment,
          informationStatus,
        };
      case "VALIDATED":
        return {
          type: "success" as const,
          title: t("treezorOnboarding.kyc.validatedTitle"),
          message: t("treezorOnboarding.kyc.validatedMessage"),
          statusLabel,
          comment,
          informationStatus,
        };
      case "REFUSED":
      case "CANCELED":
        return {
          type: "error" as const,
          title: t("treezorOnboarding.kyc.refusedTitle"),
          message: t("treezorOnboarding.kyc.refusedMessage"),
          statusLabel,
          comment,
          informationStatus,
        };
      case "INCOMPLETE":
      case "REVIEW NEEDED":
        return {
          type: "warning" as const,
          title: t("treezorOnboarding.kyc.reviewOpenTitle"),
          message: t("treezorOnboarding.kyc.reviewOpenMessage"),
          statusLabel,
          comment,
          informationStatus,
        };
    }
  }

  if (kycReadyForAdminReview.value) {
    if (!user.isKind("admin"))
      return {
        type: "info" as const,
        title: t("treezorOnboarding.kyc.ibanRequestedTitle"),
        message: t("treezorOnboarding.kyc.ibanRequestedMessage"),
        statusLabel: t("treezorOnboarding.kyc.statuses.PENDING"),
      };
    return {
      type: "info" as const,
      title: t("treezorOnboarding.kyc.readyForReviewTitle"),
      message: t("treezorOnboarding.kyc.readyForReviewMessage"),
      statusLabel: t("treezorOnboarding.kyc.readyForReviewStatus"),
    };
  }

  if (account.value?.kycAdminReview?.status === "refused") {
    return {
      type: "warning" as const,
      title: t("treezorOnboarding.kyc.adminRefusedTitle"),
      message: t("treezorOnboarding.kyc.adminRefusedMessage"),
      statusLabel: t("treezorOnboarding.kyc.adminRefusedStatus"),
      comment: account.value.kycAdminReview.reason,
    };
  }

  return undefined;
});
</script>
